import * as React from 'react';
import { useIntl } from 'react-intl';

import { Text } from '@notino/react-styleguide';

import { useAnimateUnmount } from '@components/Animate/useAnimateUnmount';
import { useGetImageAttributes } from '@components/CdnImage/useImageAttributes';

import { SHADEFINDER_CONFIG } from '../../config';
import { useSaveShadefinderUserData } from '../../hooks/useSaveShadefinderUserData';
import { useShadefinderTransitions } from '../../hooks/useShadefinderTransitions';
import { useTrackSelect } from '../../hooks/useTrackSelect';
import { messages } from '../../messages';
import {
  trackShadefinderSelectTone,
  trackShadefinderHowWeSelectClick,
} from '../../tracking';
import {
  ShadefinderDesiredShade,
  ShadefinderDesiredShadeKeys,
} from '../../types';
import { Item } from '../Item';
import { StepContainer } from '../StepContainer';
import { HowWeChooseShadesButton } from '../styled';

import { HowWeChooseShades } from './HowWeChooseShades';
import { ShadefinderStep4 } from './Step4';

export interface IShadefinderStep3Props {
  masterId: string;
  desiredShades: ShadefinderDesiredShade;
  timeOfStart: number;
}

const HOW_WE_CHOOSE_SHADES_PROPS = {
  currentStep: 3,
  showTitle: false,
  showProgress: false,
  name: 'how_we_select',
};

const STEP_4_SHADES_PROPS = {
  currentStep: 4,
  name: 'select_product',
  showTitle: false,
  showProgress: false,
};

export const ShadefinderStep3: React.FC<IShadefinderStep3Props> = ({
  masterId,
  desiredShades,
  timeOfStart,
}) => {
  const { formatMessage } = useIntl();
  const { onShadefinderUserDataChanged } = useSaveShadefinderUserData(masterId);
  const { transition, animateProgress } = useShadefinderTransitions();
  const [unmounting, animateTransition] = useAnimateUnmount();

  const getImageAttributes = useGetImageAttributes();
  const trackSelect = useTrackSelect(timeOfStart);

  const renderableOrderedDesiredShades = Object.keys(
    SHADEFINDER_CONFIG.swatches
  ).filter((key) => key in desiredShades);

  React.useEffect(() => {
    void onShadefinderUserDataChanged({
      recommendation: renderableOrderedDesiredShades.map(
        (key: ShadefinderDesiredShadeKeys) => {
          const desiredShade = desiredShades[key];

          return {
            makeupScale: key,
            variantId: desiredShade.productId,
            userSelectedVariant: false,
          };
        }
      ),
    });

    // NOTE: Execute on first render only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepContainer
      unmounting={unmounting}
      header={formatMessage(messages.lastStepTitle)}
      subheader={
        <>
          {formatMessage(messages.lastStepDescription)}

          <br />

          <HowWeChooseShadesButton
            onClick={animateTransition(
              () =>
                transition(
                  <HowWeChooseShades timeOfStart={timeOfStart} />,
                  HOW_WE_CHOOSE_SHADES_PROPS
                ),
              {
                beforeTransition: () => {
                  animateProgress(HOW_WE_CHOOSE_SHADES_PROPS);
                  trackSelect(trackShadefinderHowWeSelectClick);
                },
              }
            )}
          >
            <u>{formatMessage(messages.howWeChooseShadesButton)}</u>
          </HowWeChooseShadesButton>
        </>
      }
      footer={formatMessage(messages.makeupTip, {
        strong: (chunks) => <strong>{chunks}</strong>,
      })}
    >
      {renderableOrderedDesiredShades.map(
        (key: ShadefinderDesiredShadeKeys) => {
          const desiredShade = desiredShades[key];

          const title = formatMessage(SHADEFINDER_CONFIG.swatches[key].title);
          const description = formatMessage(
            SHADEFINDER_CONFIG.swatches[key].description
          );

          const id = `desired-shade-${key}`;
          return (
            <Item
              key={key}
              labelledby={id}
              imageUrl={
                getImageAttributes(desiredShade.swatchImage, 'detail_thumb').src
              }
              smallerImage={true}
              onClick={animateTransition(
                () =>
                  transition(
                    <ShadefinderStep4
                      recommendedVariant={desiredShade}
                      timeOfStart={timeOfStart}
                    />,
                    STEP_4_SHADES_PROPS
                  ),
                {
                  beforeTransition: () => {
                    void onShadefinderUserDataChanged({
                      recommendation: [
                        {
                          makeupScale: key,
                          variantId: desiredShade.productId,
                          userSelectedVariant: true,
                        },
                      ],
                    });
                    animateProgress(STEP_4_SHADES_PROPS);
                    trackSelect((props) =>
                      trackShadefinderSelectTone({ ...props, name: key })
                    );
                  },
                }
              )}
            >
              <Text
                typography="labelLarge400"
                block={true}
                marginBottom="0.25rem"
                color="text.primary"
                id={id}
              >
                {title}
              </Text>

              <Text typography="labelRegular400">{description}</Text>
            </Item>
          );
        }
      )}
    </StepContainer>
  );
};
